import { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Divider, Empty, Row, Space, Spin, Switch, Table, Tag, Typography } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import moment from "moment";
import ApiService from "../../core/service/api-service";
import PatientSelectModal from "./PatientSelectModal";
import OperationProgress, { OperationStatus, useOperationProgress } from "../../core/components/OperationProgress";

const STEP = {
    SELECT: 'select',
    CONFIRM: 'confirm',
}

const patientLine = (patient) => {
    const parts = [patient?.dicomPatientId, patient?.name].filter(Boolean);
    return parts.join(' — ');
}

const patientDetail = (patient, t) => {
    const parts = [];
    if (patient?.birthdate) parts.push(moment(patient.birthdate).format('DD.MM.YYYY'));
    if (patient?.gender) parts.push(t(patient.gender));
    return parts.join(' · ');
}

/**
 * Çekim birleştirme: hasta seç → ÖNCE/SONRA önizlemesini gör → onayla → canlı ilerleme.
 *
 * Onay ekranı şart: işlem geri alınamaz (RIS'te çekim hedef hastaya bağlanır, PACS'te yeni UID ile
 * kopyalanır) ve "tüm çekimler" seçeneğiyle kullanıcının grid'de seçmediği çekimler de taşınabilir.
 * Kapsam istemcide TAHMİN EDİLMEZ: önizleme sunucudan gelir ve birleştirmenin kendisiyle aynı kodla
 * hesaplanır, yani onaylanan ile uygulanan birebir aynıdır.
 */
export default function MergeStudiesModal({ studyIdList, studyInfo, canCreatePatient, onCompleted }) {
    const { t } = useTranslation();

    const [step, setStep] = useState(STEP.SELECT);
    const [targetPatient, setTargetPatient] = useState(null);
    const [allStudies, setAllStudies] = useState(false);
    const [preview, setPreview] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [merging, setMerging] = useState(false);

    const { operationId, progress, start, fail } = useOperationProgress();

    const loadPreview = async (patientId, includeAllStudies) => {
        setLoadingPreview(true);
        try {
            const res = await ApiService.Study.PreviewMergeStudiesToPatient({
                studyIdList,
                targetPatientId: patientId,
                allStudies: includeAllStudies,
            });
            setPreview(res.success ? res.data : null);
        } finally {
            setLoadingPreview(false);
        }
    }

    useEffect(() => {
        if (step === STEP.CONFIRM && targetPatient)
            loadPreview(targetPatient.id, allStudies);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, targetPatient?.id, allStudies]);

    const merge = async () => {
        setMerging(true);
        try {
            const res = await ApiService.Study.MergeStudiesToPatient({
                studyIdList,
                targetPatientId: targetPatient.id,
                allStudies,
                operationId: start(),
            });

            if (res.success)
                onCompleted && onCompleted();
            else
                fail(res.message || t('Operation failed'));
        } finally {
            setMerging(false);
        }
    }

    if (step === STEP.SELECT) {
        return (
            <PatientSelectModal
                studyInfo={studyInfo}
                canCreatePatient={canCreatePatient}
                onSelected={(patient) => {
                    setTargetPatient(patient);
                    setStep(STEP.CONFIRM);
                }}
            />
        );
    }

    const studyColumns = [
        {
            title: t('Study Date'),
            dataIndex: 'studyDateTime',
            width: 130,
            render: (value) => value ? moment(value).format('DD.MM.YYYY HH:mm') : '-',
        },
        {
            title: t('Modality'),
            dataIndex: 'modality',
            width: 80,
        },
        {
            title: t('Accession Number'),
            dataIndex: 'accessionNumber',
            width: 150,
            ellipsis: true,
        },
        {
            title: t('Study Description'),
            dataIndex: 'studyDescription',
            ellipsis: true,
        },
        {
            title: t('Result'),
            dataIndex: 'willMove',
            width: 150,
            render: (willMove, row) => (
                <Space size={4}>
                    <Tag color={willMove ? 'blue' : 'default'}>
                        {willMove ? t('Will move') : t('Stays')}
                    </Tag>
                    {row.isSelected && <Tag color={'green'}>{t('Selected')}</Tag>}
                </Space>
            ),
        },
    ];

    const running = !!operationId && progress?.status !== OperationStatus.FAILED;
    const completed = progress?.status === OperationStatus.COMPLETED;

    return (
        <div>
            <Space style={{ marginBottom: 12 }} wrap>
                <Button onClick={() => setStep(STEP.SELECT)} disabled={running}>
                    {t('Change Target Patient')}
                </Button>
                <Space>
                    <span>{t('All Studies')}</span>
                    <Switch
                        checked={allStudies}
                        onChange={setAllStudies}
                        disabled={running || loadingPreview}
                    />
                </Space>
                <Typography.Text type={'secondary'}>
                    {allStudies
                        ? t('All studies of the source patients will be moved.')
                        : t('Only the selected studies will be moved.')}
                </Typography.Text>
            </Space>

            <Spin spinning={loadingPreview}>
                {!preview ? (
                    <Empty description={t('Preview could not be loaded')} />
                ) : (
                    <>
                        <Alert
                            type={'warning'}
                            showIcon
                            message={t('Please review before confirming')}
                            description={(
                                <Space direction={'vertical'} size={2}>
                                    <span>
                                        {t('MERGE_SUMMARY_MOVE', {
                                            studies: preview.moveCount,
                                            patients: preview.sources?.length || 0,
                                        })}
                                    </span>
                                    {preview.stayCount > 0 && (
                                        <span>{t('MERGE_SUMMARY_STAY', { studies: preview.stayCount })}</span>
                                    )}
                                    {preview.emptiedPatientCount > 0 && (
                                        <span>{t('MERGE_SUMMARY_EMPTIED', { patients: preview.emptiedPatientCount })}</span>
                                    )}
                                    {preview.alreadyOnTargetCount > 0 && (
                                        <span>{t('MERGE_SUMMARY_SKIPPED', { studies: preview.alreadyOnTargetCount })}</span>
                                    )}
                                    <span>{t('MERGE_PACS_NOTE')}</span>
                                </Space>
                            )}
                            style={{ marginBottom: 12 }}
                        />

                        <Row gutter={12} align={'middle'}>
                            <Col span={11}>
                                <Typography.Text strong>{t('Before')}</Typography.Text>
                                <div style={{ maxHeight: 120, overflowY: 'auto', marginTop: 4 }}>
                                    {preview.sources?.map(source => (
                                        <div key={source.patient.id}>
                                            {patientLine(source.patient)}
                                            <Typography.Text type={'secondary'}>
                                                {` (${t('MERGE_STUDY_COUNT', { studies: source.patient.studyCount })})`}
                                            </Typography.Text>
                                        </div>
                                    ))}
                                    {!preview.sources?.length && <Typography.Text type={'secondary'}>-</Typography.Text>}
                                </div>
                            </Col>
                            <Col span={2} style={{ textAlign: 'center' }}>
                                <ArrowRightOutlined style={{ fontSize: 20 }} />
                            </Col>
                            <Col span={11}>
                                <Typography.Text strong>{t('After')}</Typography.Text>
                                <div style={{ marginTop: 4 }}>
                                    <div>{patientLine(preview.target)}</div>
                                    <Typography.Text type={'secondary'}>
                                        {patientDetail(preview.target, t)}
                                    </Typography.Text>
                                    <div>
                                        <Typography.Text type={'secondary'}>
                                            {`${preview.target.studyCount} → `}
                                        </Typography.Text>
                                        <Typography.Text strong>
                                            {t('MERGE_STUDY_COUNT', { studies: preview.target.studyCount + preview.moveCount })}
                                        </Typography.Text>
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        <Divider style={{ margin: '12px 0' }} />

                        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                            {preview.sources?.map(source => (
                                <Card
                                    key={source.patient.id}
                                    size={'small'}
                                    style={{ marginBottom: 8 }}
                                    title={(
                                        <Space wrap>
                                            <span>{patientLine(source.patient)}</span>
                                            <Typography.Text type={'secondary'}>{patientDetail(source.patient, t)}</Typography.Text>
                                            <Tag color={'blue'}>{t('MERGE_WILL_MOVE_COUNT', { studies: source.moveCount })}</Tag>
                                            {source.stayCount > 0
                                                ? <Tag>{t('MERGE_STAYS_COUNT', { studies: source.stayCount })}</Tag>
                                                : <Tag color={'orange'}>{t('No studies left')}</Tag>}
                                        </Space>
                                    )}
                                >
                                    <Table
                                        rowKey={'id'}
                                        size={'small'}
                                        columns={studyColumns}
                                        dataSource={source.studies}
                                        pagination={false}
                                    />
                                </Card>
                            ))}
                        </div>
                    </>
                )}
            </Spin>

            {operationId && (
                <div style={{ marginTop: 16 }}>
                    <OperationProgress progress={progress} title={t('Merging studies')} />
                </div>
            )}

            <div style={{ marginTop: 16, textAlign: 'right' }}>
                <Button
                    type={'primary'}
                    danger
                    loading={merging}
                    disabled={!preview || preview.moveCount === 0 || completed}
                    onClick={merge}
                >
                    {t('Confirm and Merge')}
                </Button>
            </div>
        </div>
    );
}
